/**
 * The verified mark, and the panel it opens.
 *
 * A restaurant the platform has looked at carries a bowl beside its name; one it
 * has not carries nothing — no greyed-out mark, no empty state to wonder about.
 * The mark is a control rather than a decoration, because the claim it makes
 * needs somewhere to be read: the panel says what was looked at and when, which
 * is the whole of what "verified" means here.
 *
 * It shows the restaurant's state *now*, on the storefront and on the tracking
 * card alike, so a mark that comes off comes off everywhere at once.
 *
 * @param props When the restaurant was marked, its zone, and the glyph's size.
 * @returns The mark and its panel, or null when the restaurant is not marked.
 */
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { Calendar } from 'lucide-react-native';
import { formatMonthYear } from '@yummy/shared';
import { Icon, Modal, Row, Stack, Text, VerifiedBowl, useT } from '@yummy/ui-rnr';

export function VerifiedMark({
  since,
  timeZone,
  size = 21,
}: {
  /** When the platform marked this restaurant, or null. */
  since: string | null;
  /** The restaurant's own zone, so the month is the one they were marked in. */
  timeZone: string;
  size?: number;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);

  if (!since) return null;

  return (
    <>
      {/* The glyph is 21 points and the thing a thumb hits is 44: the negative
          margin cancels the padding, so the bowl sits exactly where it looks
          like it sits and the target around it is invisible. */}
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={t('customer.verified.mark')}
        className="-m-2.5 p-2.5 active:opacity-70">
        <VerifiedBowl size={size} />
      </Pressable>

      <Modal
        visible={open}
        onClose={() => setOpen(false)}
        title={t('customer.verified.title')}
        maxWidth={420}>
        <Stack gap="md">
          <Row gap="md" align="start">
            <View className="mt-0.5">
              <VerifiedBowl size={20} />
            </View>
            <Text className="flex-1 text-sm">{t('customer.verified.reason')}</Text>
          </Row>
          <Row gap="md" align="center">
            <Icon as={Calendar} size={17} className="text-muted-foreground" aria-hidden />
            <Text className="text-muted-foreground text-sm">
              {t('customer.verified.since', { when: formatMonthYear(since, timeZone, t) })}
            </Text>
          </Row>
        </Stack>
      </Modal>
    </>
  );
}
